import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, apiBlob } from '../lib/api';
import { ErrorAlert, Loading } from '../components/ui';
import { useAuth } from '../auth';

interface TaxGroup {
  donorCode: string;
  name: string;
  taxIdLast4: string | null;
  donationCount: number;
  totalSatang: number;
  receiptNos: string[];
  blockedReason: string | null;
  hasInKind: boolean;
}

interface TaxSummary {
  taxYear: { buddhist: number; gregorian: number };
  org: { nameTh: string; taxId: string; charityAnnouncementNo: string };
  canDeduct: boolean;
  summary: {
    readyDonors: number;
    readySatang: number;
    blockedDonors: number;
    blockedSatang: number;
    receiptCount: number;
  };
  ready: TaxGroup[];
  blocked: TaxGroup[];
}

const baht = (satang: number) =>
  (satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** ปีภาษีไทยคือปีปฏิทิน — ให้เลือกย้อนหลังได้ 5 ปีตามอายุความยื่นเพิ่มเติม */
function yearOptions(): number[] {
  const nowBe = new Date().getFullYear() + 543;
  return [nowBe, nowBe - 1, nowBe - 2, nowBe - 3, nowBe - 4];
}

/**
 * สรุปยอดบริจาครายปีเพื่อยื่นสรรพากร
 *
 * หน้านี้ทำสองอย่างที่ต่างกันชัดเจน:
 *   ดูสรุป  — ไม่มีเลขประจำตัวผู้เสียภาษีเต็มเลย มีแค่ 4 ตัวท้าย
 *   โหลดไฟล์ — มีเลขเต็ม จึงจำกัดสิทธิ์แคบกว่าและถูกบันทึกว่าใครดึงไปเมื่อไร
 */
export default function TaxReport() {
  const { allowed } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear() + 543 - 1);
  const [data, setData] = useState<TaxSummary | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    setData(null);
    try {
      setData(await api<TaxSummary>(`/reports/tax-donations?year=${year}`));
    } catch (e) {
      setError(e);
    }
  }, [year]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download() {
    setDownloading(true);
    setError(null);
    try {
      const { blob } = await apiBlob(`/reports/tax-donations.csv?year=${year}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `สรุปบริจาคเพื่อลดหย่อนภาษี-${year}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>สรุปยอดบริจาคเพื่อยื่นสรรพากร</h1>
          <p>ยอดรวมต่อผู้บริจาคในปีภาษี สำหรับนำไปยื่นระบบ e-Donation</p>
        </div>
        <div className="field">
          <label htmlFor="tax-year">ปีภาษี (พ.ศ.)</label>
          <select id="tax-year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {yearOptions().map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>

      <ErrorAlert error={error} />

      {!data ? (
        <Loading />
      ) : (
        <>
          {!data.canDeduct && (
            <div className="alert error">
              <strong>ยังกรอกเลขลำดับที่ในประกาศกระทรวงการคลังไม่ครบ</strong>
              <br />
              ถ้ามูลนิธิไม่อยู่ในประกาศว่าด้วยองค์การหรือสถานสาธารณกุศล
              <strong> ผู้บริจาคนำไปลดหย่อนภาษีไม่ได้เลย</strong> ต่อให้ออกใบเสร็จครบทุกใบ —
              ตรวจสถานะกับกรมสรรพากรก่อน แล้วกรอกเลขที่{' '}
              <Link to="/settings">หน้าตั้งค่าระบบ</Link>
            </div>
          )}

          <div className="stat-row">
            <div className="stat">
              <div className="stat-label">ยื่นได้</div>
              <div className="stat-value">{baht(data.summary.readySatang)}</div>
              <div className="stat-sub">{data.summary.readyDonors} ราย</div>
            </div>
            <div className="stat">
              <div className="stat-label">ติดปัญหา ยังยื่นไม่ได้</div>
              <div className={`stat-value ${data.summary.blockedSatang ? 'danger' : ''}`}>
                {baht(data.summary.blockedSatang)}
              </div>
              <div className="stat-sub">{data.summary.blockedDonors} ราย</div>
            </div>
            <div className="stat">
              <div className="stat-label">ใบเสร็จที่นับ</div>
              <div className="stat-value">{data.summary.receiptCount}</div>
              <div className="stat-sub">ปี {data.taxYear.buddhist}</div>
            </div>
          </div>

          <div className="alert info">
            นับเฉพาะเงินที่ <strong>ตรวจสลิปแล้ว</strong> และ{' '}
            <strong>ออกใบเสร็จแล้วโดยใบเสร็จยังไม่ถูกยกเลิก</strong> เท่านั้น
            เงินที่ยังไม่ตรวจหรือยังไม่มีใบเสร็จไม่ใช่หลักฐานการบริจาคในทางภาษี จึงไม่เข้ารายงานนี้
          </div>

          {allowed('report:export') && (
            <div className="btn-row">
              <button className="btn" disabled={downloading} onClick={() => void download()}>
                {downloading ? 'กำลังสร้างไฟล์…' : 'ดาวน์โหลดไฟล์สำหรับยื่น (CSV)'}
              </button>
            </div>
          )}
          <p className="hint">
            ไฟล์นี้มีเลขประจำตัวผู้เสียภาษีของผู้บริจาคแบบเต็ม — เก็บให้ดี อย่าส่งต่อทางแชท
            และทุกครั้งที่ดาวน์โหลดจะถูกบันทึกไว้ว่าใครดึงไปเมื่อไร
            <br />
            ก่อนอัปโหลดเข้าระบบ e-Donation ให้เทียบหัวคอลัมน์กับแบบฟอร์มล่าสุดของกรมสรรพากรก่อน
            เพราะรูปแบบไฟล์เปลี่ยนได้โดยที่ระบบเราไม่รู้
          </p>

          <div className="card">
            <h2>ยื่นได้ ({data.ready.length} ราย)</h2>
            {data.ready.length === 0 ? (
              <p className="muted">ยังไม่มีรายการที่พร้อมยื่นในปีภาษีนี้</p>
            ) : (
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>รหัส</th>
                      <th>ชื่อผู้บริจาค</th>
                      <th>เลขภาษี (4 ตัวท้าย)</th>
                      <th className="num">ใบเสร็จ</th>
                      <th className="num">ยอดรวม (บาท)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ready.map((g) => (
                      <tr key={g.donorCode + g.name}>
                        <td className="mono">{g.donorCode}</td>
                        <td>
                          {g.name}
                          {g.hasInKind && (
                            <span className="badge amber" title="มีการบริจาคสิ่งของรวมอยู่ด้วย">
                              มีสิ่งของ
                            </span>
                          )}
                        </td>
                        <td className="mono">{g.taxIdLast4 ?? '—'}</td>
                        <td className="num">{g.donationCount}</td>
                        <td className="num">{baht(g.totalSatang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {data.blocked.length > 0 && (
            <div className="card">
              <h2>ติดปัญหา — ยังยื่นให้ไม่ได้ ({data.blocked.length} ราย)</h2>
              <p className="hint">
                รายการเหล่านี้มีใบเสร็จถูกต้องแล้ว แต่ยังขาดข้อมูลที่ต้องใช้ยื่น
                แก้ให้ครบแล้วกลับมากดสร้างรายงานใหม่ ยอดจะย้ายขึ้นไปอยู่ด้านบนเอง
              </p>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>รหัส</th>
                      <th>ชื่อผู้บริจาค</th>
                      <th>ติดตรงไหน</th>
                      <th className="num">ยอดรวม (บาท)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.blocked.map((g) => (
                      <tr key={g.donorCode + g.name}>
                        <td className="mono">{g.donorCode}</td>
                        <td>{g.name}</td>
                        <td>{g.blockedReason}</td>
                        <td className="num">{baht(g.totalSatang)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="alert warn">
            <strong>ระบบช่วยรวบรวมตัวเลขให้เท่านั้น ไม่ได้ให้คำแนะนำทางภาษี</strong> —
            เงื่อนไขการลดหย่อน สิทธิของผู้บริจาคแต่ละราย และรูปแบบไฟล์ที่กรมสรรพากรรับ
            ให้ยืนยันกับกรมสรรพากรหรือผู้ทำบัญชีของมูลนิธิก่อนยื่นทุกครั้ง
          </div>
        </>
      )}
    </>
  );
}
